/**
 * @file Image 图片与预览
 * @description 缩略图展示；点击全屏预览（遮罩 + fade）。
 *
 * @whenToUse 列表缩略图、详情配图、上传结果预览。
 * @whenNotToUse 纯装饰背景图（直接用 img / CSS）。
 */

import { useEffect, useState, type ImgHTMLAttributes } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn.ts'

export type ImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'onClick'> & {
  /** 是否可点击预览，默认 true */
  preview?: boolean
  /** 预览大图地址；默认用 src */
  previewSrc?: string
  /** 外层 class（圆角等） */
  wrapperClassName?: string
}

export function Image({
  preview = true,
  previewSrc,
  wrapperClassName,
  className,
  alt = '',
  src,
  ...rest
}: ImageProps) {
  const [open, setOpen] = useState(false)
  const [visible, setVisible] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    if (open) {
      setMounted(true)
      const frame = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(frame)
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), 200)
    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <>
      <button
        type="button"
        disabled={!preview || !src}
        className={cn(
          'inline-block overflow-hidden rounded-lg border border-border bg-surface p-0 transition-opacity duration-200',
          preview && src && 'cursor-zoom-in hover:opacity-90',
          !preview && 'cursor-default',
          wrapperClassName,
        )}
        onClick={() => {
          if (preview && src) setOpen(true)
        }}
      >
        <img
          src={src}
          alt={alt}
          className={cn('block max-w-full object-cover', className)}
          {...rest}
        />
      </button>

      {mounted && src && typeof document !== 'undefined'
        ? createPortal(
            <div
              className={cn(
                'fixed inset-0 z-50 flex items-center justify-center bg-canvas/80 backdrop-blur-sm transition-opacity duration-200',
                visible ? 'opacity-100' : 'opacity-0',
              )}
              role="dialog"
              aria-modal
              aria-label="图片预览"
              onClick={() => setOpen(false)}
            >
              <button
                type="button"
                aria-label="关闭预览"
                className="absolute top-4 right-4 inline-flex size-9 items-center justify-center rounded-lg border border-border bg-raised text-foreground transition-opacity duration-200 hover:opacity-90"
                onClick={() => setOpen(false)}
              >
                <X className="size-4" aria-hidden />
              </button>
              <img
                src={previewSrc ?? src}
                alt={alt}
                className="max-h-[85vh] max-w-[90vw] rounded-xl object-contain"
                onClick={(event) => event.stopPropagation()}
              />
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
